import { useEffect, useState } from "react";
import { Button } from "@oc-labs/wt-storybook";
import useCompany from "../../../hooks/useCompany";
import useAuth from "../../../hooks/useAuth";
import { BanIcon, Clock10Icon, CopyIcon, PlusIcon, RefreshCwIcon, MailIcon } from "lucide-react";
import InviteAdminModal from "../../../components/invite-admin-modal";
import useModal from "../../../hooks/useModal";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import ConfirmModal from "../../../components/modals/ConfirmModal";

const ROLES = ["member", "admin", "owner"];

export default function Users() {
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const { getCompanyMembers, getInvitations, inviteMember, cancelInvitation, updateMemberRole, removeMember } = useCompany();
  const { user: currentUser } = useAuth();
  const { open: openInviteAdminModal } = useModal('invite-admin');
  const { open: openConfirmModal } = useModal('confirm-modal');

  // Find current user's role in the org
  const currentMember = users.find((m) => m.userId === currentUser?.id);
  const myRole = currentMember?.role?.toLowerCase();
  const canManageRoles = myRole === "owner" || myRole === "admin";

  useEffect(() => {
    loadMembers();
    loadInvitations();
  }, []);

  const loadMembers = async () => {
    try {
      const fetchedUsers = await getCompanyMembers(0);
      setUsers(fetchedUsers.members || []);
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const loadInvitations = async () => {
    try {
      const fetchedInvitations = await getInvitations();
      setInvitations(fetchedInvitations);
    } catch (error) {
      console.error("Error loading invitations:", error);
    }
  }

  const handleResendInvite = async (invitation) => {
    await inviteMember(invitation.email);
    toast.success("Invitation resent successfully");
    loadInvitations();
  };

  const handleCancelInvite = async (invitation) => {
    await cancelInvitation(invitation.id);
    toast.success("Invitation cancelled successfully");
    loadInvitations();
  };

  const buildInvitationUrl = (invitation) => {
    const invitationToken = invitation?.token || invitation?.id;
    if (!invitationToken) return "/auth/accept-invitation";
    return `/auth/accept-invitation/${invitationToken}?organizationInvitationToken=${invitationToken}`;
  };

  const handleCopyInvitationLink = async (invitation) => {
    try {
      const invitationUrl = buildInvitationUrl(invitation);
      const absoluteUrl = `${window.location.origin}${invitationUrl}`;
      await navigator.clipboard.writeText(absoluteUrl);
      toast.success("Invitation link copied");
    } catch (error) {
      console.error("Error copying invitation link:", error);
      toast.error("Could not copy invitation link");
    }
  };

  const handleRoleChange = (member, newRole) => {
    openConfirmModal({
      title: "Change Role",
      description: `Are you sure you want to change ${member.user?.name || member.user?.email}'s role to ${newRole}?`,
      onConfirm: async () => {
        try {
          await updateMemberRole(member.id, newRole);
          toast.success(`Role updated to ${newRole}`);
          loadMembers();
        } catch {
          toast.error("Failed to update role");
          loadMembers();
        }
      },
    });
  };

  const handleRemoveMember = (member) => {
    openConfirmModal({
      title: "Remove Member",
      description: `Are you sure you want to remove ${member.user?.name || member.user?.email} from the organization?`,
      onConfirm: async () => {
        try {
          await removeMember(member.id);
          toast.success("Member removed");
          loadMembers();
        } catch {
          toast.error("Failed to remove member");
        }
      },
    });
  };

  const getRoleBadgeColor = (role) => {
    if (role === "owner" || role === "Owner") return "bg-blue-50 text-blue-600";
    if (role === "admin" || role === "Admin") return "bg-violet-50 text-violet-600";
    return "bg-slate-100 text-slate-600";
  };

  return (
    <>
    <ConfirmModal />
    <InviteAdminModal onInviteSuccess={loadInvitations} />
      <main className="flex-1">
        <div className="2xl:p-8 p-4">
          <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
            <div>
              <h2 className="2xl:text-2xl text-lg font-bold">Manage Admin Users</h2>
              <p className="text-slate-500">
                Add, remove or edit permissions for your team members.
              </p>
            </div>
            <div>
              <Button
                onClick={() => openInviteAdminModal()}
                variant="secondary"
                className="hidden md:flex"
              >
                <PlusIcon className="w-4 h-4" />
                Invite Member
              </Button>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm mb-8 overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[500px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 ">
                  <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Member
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((member, index) => (
                  <tr 
                    key={member.id || index}
                    className="hover:bg-slate-50/50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-slate-200 overflow-hidden shrink-0">
                          {member.avatar ? (
                            <img
                              alt={`${member.name} Profile`}
                              src={member.avatar}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-500 font-semibold">
                              {member.name?.charAt(0)?.toUpperCase() || "U"}
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-sm truncate">{member?.user?.name}</div>
                          <div className="text-xs text-slate-500 truncate">{member.user?.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {canManageRoles && member.userId !== currentUser?.id ? (
                        <select
                          value={member.role?.toLowerCase() || "member"}
                          onChange={(e) => handleRoleChange(member, e.target.value)}
                          className={`text-sm px-2 py-1 rounded-md font-medium border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-200 ${getRoleBadgeColor(member.role)} capitalize`}
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                      ) : (
                        <span className={`text-sm px-2 py-1 rounded-md font-medium ${getRoleBadgeColor(member.role)} capitalize`}>
                          {member.role || "Member"}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {canManageRoles && member.userId !== currentUser?.id && (
                        <button
                          onClick={() => handleRemoveMember(member)}
                          className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                          title="Remove member"
                        >
                          <BanIcon className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}

                {(() => {
                  const pendingInvitations = invitations.filter(inv => inv.status === "pending");
                  return (
                    <tr className="bg-white">
                      <td className="px-6 py-4" colSpan="5">
                        <div className="flex items-center gap-2 text-sm font-semibold text-slate-500 mb-4">
                          <Clock10Icon className="w-4 h-4" />
                          Pending Invitations
                        </div>
                        <div className="space-y-3">
                          {pendingInvitations.length === 0 ? (
                            <div className="text-sm text-slate-400">No pending invitations</div>
                          ) : (
                            pendingInvitations.map((invitation, index) => {
                              const invitationUrl = buildInvitationUrl(invitation);

                              return (
                              <div key={invitation.id || index} className="flex items-center py-1">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600">
                                    <MailIcon className="w-4 h-4" />
                                  </div>
                                  <div>
                                    <div className="text-sm font-semibold flex items-center gap-2">
                                      {invitation.email}
                                      {/* Enlace de invitación */}
                                      <Link
                                        to={invitationUrl}
                                        className="text-xs text-blue-600 underline ml-2"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        Invitation Link
                                      </Link>
                                      <button
                                        onClick={() => handleCopyInvitationLink(invitation)}
                                        className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors cursor-pointer"
                                        title="Copy Invitation Link"
                                      >
                                        <CopyIcon className="w-4 h-4" />
                                      </button>
                                      <button 
                                        onClick={() => handleResendInvite(invitation)}
                                        className="p-1 text-slate-400 hover:text-primary hover:bg-slate-100 rounded-md transition-colors cursor-pointer" 
                                        title="Resend Invite"
                                      >
                                        <RefreshCwIcon className="w-4 h-4" />
                                      </button>
                                      <button 
                                        onClick={() => handleCancelInvite(invitation)}
                                        className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors cursor-pointer" 
                                        title="Cancel"
                                      >
                                        <BanIcon className="w-4 h-4" />
                                      </button>
                                    </div>
                                    <div className="text-xs text-slate-500">
                                      Sent {invitation.sentDate || "recently"}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )})
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })()}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </>
  );
}